/**
 * impersonationService - thin axios wrappers for the SuperAdmin
 * impersonation API (PR-R3 + backend Phase 1).
 *
 * Mechanism (token-stamp, NOT token-swap):
 *   The platform has no server-side sessions. Impersonation state lives on
 *   the SAME bearer token's `tokens` row as `impersonated_distributor_id`.
 *   There is NO second token and NO token swap: while that column is set,
 *   the backend's _set_request_auth_context resolves the impersonated
 *   distributor's Owner capabilities (with superadmin.* STRIPPED) and the
 *   same bearer token transparently behaves as the tenant.
 *
 * Contract:
 *   POST /impersonate/start  body: { distributor_id: int }
 *     -> 200 { account_id, capabilities: [...], distributor: {id, feature_flags} }
 *     Stamps the token. Requires superadmin.impersonate; 404 on unknown
 *     distributor. The bearer token is NOT echoed in the body.
 *
 *   POST /impersonate/stop   body: (none)
 *     -> 200 { account_id, capabilities: [...], distributor }
 *     Clears the stamp. Idempotent. The bearer token is NOT echoed.
 *
 *   GET /impersonate/status  body: (none)
 *     -> 200 { active: bool, distributor_id: int|null }
 *     Reports whether the bearer token is currently impersonating. This is
 *     the authoritative source for re-deriving impersonation state after a
 *     page reload (the store is memory-only; see impersonation.js
 *     hydrate()). It returns no distributor NAME -- the store restores the
 *     banner label from a localStorage breadcrumb written at start.
 *
 * Audit logging: the backend emits an `impersonation`-category audit row on
 * both start and stop (co-located with the privilege transition). The
 * frontend does NOT post audit rows directly.
 *
 * Callers ignore the response body: the impersonation store sets its own
 * local state from its arguments, and the page reloads after start/stop so
 * the SPA rehydrates its capability set from a fresh request. These wrappers
 * exist so the store has a stable call surface.
 *
 * Conventions (matches accountService.js / aclAdminService.js):
 *   - Raw `api` (default import) - no apiCall helper
 *   - Methods return the axios promise; callers await + read .data
 *   - No try/catch - errors propagate
 */
import api from './api'
import {
    SKIP_AUTH_RECOVERY,
    SUPPRESS_ERROR_TOAST,
} from '@/interceptors/authResponseInterceptor'

// Deep-link auto-switches (router/deeplinkImpersonation.js) pass
// `{ deeplink: true }`, sent as the body hint `deeplink: true`. The SERVER
// decides what that means: it writes the fixed audit marker
// `after_value.via = "deeplink"` and copies nothing from the request (see
// `_deeplink_marker` in api/src/endpoints/impersonation.py). Manual switches
// send no hint, so their requests are unchanged.
function isDeeplink(opts) {
    return opts?.deeplink === true
}

function deeplinkHint(opts) {
    return isDeeplink(opts) ? { deeplink: true } : {}
}

/**
 * Start an impersonation session for the given distributor.
 * @param {number} distributorId
 * @returns {Promise<AxiosResponse>}
 */
export function startImpersonationApi(distributorId, opts) {
    return api.post('/impersonate/start', { distributor_id: distributorId, ...deeplinkHint(opts) })
}

/**
 * Stop the active impersonation session.
 * @returns {Promise<AxiosResponse>}
 */
export function stopImpersonationApi(opts) {
    return isDeeplink(opts)
        ? api.post('/impersonate/stop', { deeplink: true })
        : api.post('/impersonate/stop')
}

/**
 * Report whether the bearer token is currently impersonating.
 * @returns {Promise<AxiosResponse>} data: { active, distributor_id }
 */
export function getImpersonationStatusApi() {
    // A best-effort boot probe (App.vue): a 401 here means "not
    // impersonating", never "end this session" -- see SKIP_AUTH_RECOVERY.
    return api.get('/impersonate/status', { [SKIP_AUTH_RECOVERY]: true })
}

// ---------------------------------------------------------------------------
// CLIENT impersonation (card https://trello.com/c/8eq8uc8E)
// ---------------------------------------------------------------------------
//
// The exact parallel of the distributor calls above, for CLIENT (buyer-org)
// impersonation. Same token-stamp mechanism -- the same bearer token carries
// impersonation via tokens.impersonated_client_id, resolving the Client's
// primary-contact capabilities (superadmin.* STRIPPED). Distributor and
// client impersonation are mutually exclusive: starting a client
// impersonation clears any distributor stamp server-side.
//
// Contract:
//   POST /impersonate-client/start  body: { client_id: int }
//     -> 200 { account_id, capabilities, distributor, impersonated_client: {id, name} }
//     Requires superadmin.impersonate; 404 unknown client; 422 inactive
//     client / client with no active primary contact. Token NOT echoed.
//   POST /impersonate-client/stop   body: (none) -> 200, token NOT echoed.
//   GET  /impersonate-client/status -> 200 { active: bool, client_id: int|null }.

/**
 * Start a client-impersonation session for the given client.
 * @param {number} clientId
 * @returns {Promise<AxiosResponse>}
 */
export function startClientImpersonationApi(clientId, opts) {
    return api.post('/impersonate-client/start', { client_id: clientId, ...deeplinkHint(opts) })
}

/**
 * Stop the active client-impersonation session.
 * @returns {Promise<AxiosResponse>}
 */
export function stopClientImpersonationApi(opts) {
    return isDeeplink(opts)
        ? api.post('/impersonate-client/stop', { deeplink: true })
        : api.post('/impersonate-client/stop')
}

/**
 * Report whether the bearer token is currently impersonating a client.
 * @returns {Promise<AxiosResponse>} data: { active, client_id }
 */
export function getClientImpersonationStatusApi() {
    return api.get('/impersonate-client/status', { [SKIP_AUTH_RECOVERY]: true })
}

// ---------------------------------------------------------------------------
// Distributor "View as client" (card https://trello.com/c/k8m2iLzG)
// ---------------------------------------------------------------------------
//
// The distributor-facing, OWNERSHIP-SCOPED start endpoint. It uses the SAME
// token-stamp mechanism + the SAME token column (tokens.impersonated_client_id)
// as the SA client-impersonation path, so STOP and STATUS reuse the client
// endpoints above (stopClientImpersonationApi / getClientImpersonationStatusApi)
// -- those only ever touch the caller's OWN token, so there is nothing
// distributor-specific about clearing/reading the stamp. Only START differs:
// this endpoint enforces that the target client belongs to the caller's own
// distributor (404 on a cross-tenant client), gated on
// distributor.impersonate_customer instead of the SA-only
// superadmin.impersonate.
//
// Contract:
//   POST /view-as-customer/start  body: { client_id: int }
//     -> 200 { account_id, capabilities, distributor, impersonated_client: {id, name} }
//     Requires distributor.impersonate_customer; 404 unknown OR cross-tenant
//     client (existence oracle closed); 422 inactive client / no portal-capable
//     primary contact. Token NOT echoed.

/**
 * Start a "view as customer" session for one of the caller's OWN clients.
 * @param {number} clientId
 * @returns {Promise<AxiosResponse>}
 */
export function startViewAsCustomerApi(clientId, opts) {
    return api.post('/view-as-customer/start', { client_id: clientId, ...deeplinkHint(opts) })
}

// ---------------------------------------------------------------------------
// Deep-link owner resolver (Jake, 2026-10-27)
// ---------------------------------------------------------------------------
//
// GET /auth/deeplink-owner?type=<registry type>&id=<int>
//   -> 200 { actor: 'superadmin'|'distributor'|'client',
//            owner: { kind: 'distributor'|'client', id, name },
//            impersonating: { kind: 'distributor'|'client'|null, id } }
//   -> 404 for EVERY refusal (not yours, missing, malformed) -- one body, so
//      it cannot be used to learn that a foreign row exists.
// Read-only: it never starts an impersonation. router/deeplinkImpersonation.js
// decides, then calls the start/stop endpoints above. A best-effort probe:
// a refusal sends the user to their role landing, so neither a 401 nor a 429
// may end the session or raise a toast.

/**
 * Resolve the owner of a deep-linked resource for the signed-in caller.
 * @param {string} type registry type declared on the route (meta.deeplinkOwner)
 * @param {string|number} id the resource id from the route params
 * @returns {Promise<AxiosResponse>}
 */
export function getDeeplinkOwnerApi(type, id) {
    return api.get('/auth/deeplink-owner', {
        params: { type, id },
        [SKIP_AUTH_RECOVERY]: true,
        [SUPPRESS_ERROR_TOAST]: true,
    })
}
